import { Link } from 'react-router-dom';
import { VERTICALS, verticalCopy } from '@/lib/verticals';
import { useT, useRaw } from '@/lib/i18n';
import { ArrowUpRight } from 'lucide-react';

export const UniverseSection = () => {
  const t = useT();
  const raw = useRaw();
  return (
    <section
      id="univers"
      data-testid="section-universes"
      className="relative py-20 md:py-28"
    >
      <div className="om-container">
        <div className="max-w-2xl">
          <span className="om-eyebrow">{t('home.universes.eyebrow')}</span>
          <h2
            className="om-h1 mt-5 text-[36px] sm:text-[46px] lg:text-[56px] text-[var(--om-ink)]"
            data-testid="universes-title"
          >
            {t('home.universes.title_l1')}<br />{t('home.universes.title_l2')}
          </h2>
          <p className="mt-5 max-w-xl text-base md:text-lg leading-relaxed text-[var(--om-muted)]">
            {t('home.universes.subtitle')}
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {VERTICALS.map((v, i) => {
            const vc = verticalCopy(raw, v.code);
            return (
              <Link
                key={v.code}
                to={v.path}
                data-testid={`universe-card-${v.code}`}
                className="group relative flex flex-col overflow-hidden rounded-3xl border border-[var(--om-border)] bg-white/70 p-6 shadow-[0_1px_0_rgba(255,255,255,0.9)_inset,0_10px_30px_-20px_rgba(10,14,26,0.12)] transition-all duration-300 hover:-translate-y-1 hover:border-transparent"
                style={{ animationDelay: `${i * 0.08}s` }}
              >
                <div
                  aria-hidden
                  className="pointer-events-none absolute -top-10 right-[-30%] h-64 w-64 rounded-full opacity-40 blur-3xl transition-all duration-500 group-hover:opacity-90 group-hover:scale-110"
                  style={{ background: `radial-gradient(closest-side, ${v.glow}, transparent 70%)` }}
                />

                <div className="relative flex items-start justify-between">
                  <span
                    className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wider"
                    style={{ color: v.color, background: v.tint }}
                  >
                    <span className="h-1.5 w-1.5 rounded-full" style={{ background: v.color }} />
                    {vc.short}
                  </span>
                  <span className="rounded-full border border-[var(--om-border)] bg-white/80 p-2 opacity-60 transition-all duration-300 group-hover:opacity-100 group-hover:border-transparent group-hover:bg-[var(--om-ink)] group-hover:text-white">
                    <ArrowUpRight size={16} strokeWidth={2.4} />
                  </span>
                </div>

                <div className="relative mt-6 flex items-center justify-center">
                  <img
                    src={v.illustration}
                    alt={vc.label}
                    width={520}
                    height={520}
                    loading="lazy"
                    data-testid={`universe-illustration-${v.code}`}
                    className="h-40 w-40 object-contain transition-transform duration-500 group-hover:scale-105"
                    draggable={false}
                  />
                </div>

                <div className="relative mt-6">
                  <h3 className="text-[22px] font-bold tracking-tight text-[var(--om-ink)]">
                    {vc.label}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-[var(--om-muted)]">
                    {vc.tagline}
                  </p>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
};
